import { z } from "zod";
import { expenseCategorySchema } from "./category.js";
import { CURRENCIES } from "./money.js";
import { avatarTintSchema, displayNameSchema, nicknameSchema } from "./identity.js";

/** Tinte pastello che colorano un gruppo, dai token del design system. */
/**
 * Un gruppo è una cosa che si crea e si nomina; una coppia è il legame fra due
 * persone che si sono aggiunte fra gli amici, e tiene le loro spese a due.
 *
 * Una coppia **non diventa mai** un gruppo: per stare in tre si crea un gruppo,
 * esplicitamente, e sarà un'altra cosa con i suoi conti. È la ragione per cui
 * questa è una colonna e non un conteggio di membri.
 */
export const GROUP_KINDS = ["group", "pair"] as const;
export type GroupKind = (typeof GROUP_KINDS)[number];
export const groupKindSchema = z.enum(GROUP_KINDS);

export const GROUP_TINTS = ["lavender", "grey", "sky", "sand", "blush", "mint"] as const;
export type GroupTint = (typeof GROUP_TINTS)[number];
export const groupTintSchema = z.enum(GROUP_TINTS);

/**
 * `owner` può modificare ed eliminare il gruppo e gestire i ruoli.
 * Un gruppo ha sempre almeno un owner: è un invariante applicativo.
 */
export const MEMBER_ROLES = ["owner", "member"] as const;
export type MemberRole = (typeof MEMBER_ROLES)[number];
export const memberRoleSchema = z.enum(MEMBER_ROLES);

export const groupNameSchema = z
  .string()
  .trim()
  .min(1, "Il nome del gruppo non può essere vuoto")
  .max(60, "Al massimo 60 caratteri");

export const placeholderNameSchema = z
  .string()
  .trim()
  .min(1, "Il nome non può essere vuoto")
  .max(60, "Al massimo 60 caratteri");

export const currencySchema = z.enum(CURRENCIES);

// --- Risposte ---------------------------------------------------------------

/**
 * Un membro è **o** un utente reale **o** un segnaposto: una persona che
 * partecipa alle divisioni senza avere un account. Il campo `kind` rende la
 * distinzione esplicita anche sul filo, così il client non deve dedurla da
 * quale campo è nullo.
 */
export const groupMemberResponseSchema = z.discriminatedUnion("kind", [
  z.object({
    kind: z.literal("user"),
    id: z.string(),
    userId: z.string(),
    displayName: z.string(),
    nickname: z.string().nullable(),
    avatarTint: avatarTintSchema,
    role: memberRoleSchema,
    joinedAt: z.string(),
  }),
  z.object({
    kind: z.literal("placeholder"),
    id: z.string(),
    displayName: z.string(),
    role: z.literal("member"),
    joinedAt: z.string(),
  }),
]);
export type GroupMemberResponse = z.infer<typeof groupMemberResponseSchema>;

export const groupResponseSchema = z.object({
  id: z.string(),
  name: z.string(),
  tint: groupTintSchema,
  defaultCurrency: currencySchema,
  simplifyDebts: z.boolean(),
  memberCount: z.number().int().nonnegative(),
  /** Ruolo di chi ha fatto la richiesta dentro questo gruppo. */
  myRole: memberRoleSchema,
  /** Spese non eliminate. Zero significa gruppo appena creato, non «in pari». */
  expenseCount: z.number().int().nonnegative(),
  /** Saldo di chi guarda: positivo = ti devono, negativo = devi. */
  myBalanceCents: z.number().int(),
  /**
   * I primi nomi del gruppo, per la pila di avatar. Ne bastano pochi: la pila
   * ne mostra tre e `memberCount` dice quanti sono in tutto.
   */
  memberNames: z.array(z.string()),
  /**
   * L'ultima cosa successa nel gruppo — spesa o pagamento, la più recente.
   *
   * Il saldo si muove di rado, questo cambia ogni giorno: è ciò che dà una
   * ragione per riaprire l'app. `null` su un gruppo dove non è successo niente.
   */
  lastMovement: z
    .object({
      kind: z.enum(["expense", "settlement"]),
      /** La descrizione della spesa, oppure «chi → chi» per un pagamento. */
      label: z.string(),
      /** Per l'icona. `null` sui pagamenti, che non hanno categoria. */
      category: expenseCategorySchema.nullable(),
      amountCents: z.number().int(),
      occurredAt: z.iso.datetime(),
    })
    .nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type GroupResponse = z.infer<typeof groupResponseSchema>;

/**
 * Il dettaglio **non** porta saldo e numero di spese.
 *
 * Servono alla card della lista, che deve rispondere a «devo o mi devono»
 * senza entrare nel gruppo. Dentro al gruppo ci sono già `/balances` e
 * `/expenses`, che dicono la stessa cosa in modo più ricco: duplicarli qui
 * significherebbe due fonti per lo stesso numero, e prima o poi due numeri.
 */
export const groupDetailResponseSchema = groupResponseSchema
  .omit({ expenseCount: true, myBalanceCents: true, memberNames: true, lastMovement: true })
  .extend({ members: z.array(groupMemberResponseSchema) });
export type GroupDetailResponse = z.infer<typeof groupDetailResponseSchema>;

// --- Richieste --------------------------------------------------------------

export const createGroupRequestSchema = z.object({
  name: groupNameSchema,
  tint: groupTintSchema.default("grey"),
  defaultCurrency: currencySchema.default("EUR"),
  /**
   * Nomi dei segnaposto da creare insieme al gruppo. La schermata «Chi c'è?»
   * permette di aggiungere persone subito dopo la creazione, in un passaggio
   * solo dal punto di vista dell'utente.
   */
  placeholders: z.array(placeholderNameSchema).max(50).default([]),
});
export type CreateGroupRequest = z.infer<typeof createGroupRequestSchema>;

export const updateGroupRequestSchema = z
  .object({
    name: groupNameSchema.optional(),
    tint: groupTintSchema.optional(),
    defaultCurrency: currencySchema.optional(),
    simplifyDebts: z.boolean().optional(),
  })
  .refine(
    (value) => Object.values(value).some((field) => field !== undefined),
    "Serve almeno un campo da aggiornare",
  );
export type UpdateGroupRequest = z.infer<typeof updateGroupRequestSchema>;

/** Si aggiunge un segnaposto per nome, oppure un utente esistente per nickname. */
export const addMemberRequestSchema = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("placeholder"), name: placeholderNameSchema }),
  z.object({ kind: z.literal("user"), nickname: nicknameSchema }),
]);
export type AddMemberRequest = z.infer<typeof addMemberRequestSchema>;

export const updateMemberRequestSchema = z.object({
  role: memberRoleSchema,
});
export type UpdateMemberRequest = z.infer<typeof updateMemberRequestSchema>;

export const renamePlaceholderRequestSchema = z.object({
  name: displayNameSchema,
});
export type RenamePlaceholderRequest = z.infer<typeof renamePlaceholderRequestSchema>;
